/**
 * Typed boost ineligibility reason codes — must stay in sync with
 * Boosts::Errors::Reasons in social-v1. The backend emits each reason as
 * `{ error_code, error_message }`; the frontend renders localized copy
 * keyed off `error_code`.
 *
 * Renaming any of these literals is a wire-breaking change.
 *
 * TODO(TMT-XXX): the FE list is hand-mirrored from social-v1; there is no
 * automated drift detection. Either generate this list from a Rails-side
 * manifest, or add a contract test that diffs against an API endpoint.
 */
const BOOST_REASON_CODES = [
  "ACCOUNT_TOO_NEW",
  "UNVERIFIED_EMAIL",
  "ACCOUNT_SUSPENDED",
  "ACCOUNT_SILENCED",
  "ACTIVE_BOOST_EXISTS",
  "REPLY_OR_REBLOG",
  "NOT_PUBLIC",
  "NOT_OWNER",
  "MEDIA_PROCESSING",
  "MEDIA_FAILED",
  "SOLD_OUT",
] as const;

type BoostReasonCode = (typeof BOOST_REASON_CODES)[number];

/**
 * Named lookup for the reason codes so consumers reference a value instead of
 * re-typing the UPPERCASE wire literal. Derived from `BOOST_REASON_CODES` so it
 * cannot drift from the allowlist (each key maps to itself).
 */
const BOOST_REASON_CODE = Object.fromEntries(
  BOOST_REASON_CODES.map((code) => [code, code]),
) as { readonly [K in BoostReasonCode]: K };

const BOOST_REASON_CODE_SET: ReadonlySet<string> = new Set<string>(BOOST_REASON_CODES);

function isBoostReasonCode(value: string): value is BoostReasonCode {
  return BOOST_REASON_CODE_SET.has(value);
}

// camelCased domain projection of `ApiErrorReason` from `@/types/api`.
// `errorCode` is `string`, not `BoostReasonCode`, because the backend may emit
// a code the FE doesn't yet know (forward compat). Consumers must narrow with
// `isBoostReasonCode` before indexing into typed lookups like
// `boostReasonMessages`.
type BoostReason = {
  errorCode: string;
  errorMessage: string;
};

export {
  BOOST_REASON_CODE,
  isBoostReasonCode,
  type BoostReason,
  type BoostReasonCode,
};
